
import { useState, useCallback, useEffect } from 'react';

interface UsePortfolioSectionStateProps {
  defaultExpanded?: boolean;
  portfolioId?: string;
  onToggle?: (isExpanded: boolean) => void;
  persistState?: boolean;
}

export function usePortfolioSectionState({
  defaultExpanded = true,
  portfolioId = 'default',
  onToggle,
  persistState = false
}: UsePortfolioSectionStateProps = {}) {
  // Initialize state with persisted value if available
  const storageKey = `portfolio-section-${portfolioId}-expanded`;
  
  const getInitialState = useCallback(() => {
    if (!persistState) return defaultExpanded;
    
    try {
      const storedValue = localStorage.getItem(storageKey);
      return storedValue ? JSON.parse(storedValue) : defaultExpanded;
    } catch (error) {
      console.error('Error reading portfolio section state:', error);
      return defaultExpanded;
    }
  }, [defaultExpanded, persistState, storageKey]);

  const [isExpanded, setIsExpanded] = useState<boolean>(getInitialState);
  
  // Store state in localStorage when it changes
  useEffect(() => {
    if (persistState) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(isExpanded));
      } catch (error) {
        console.error('Error saving portfolio section state:', error);
      }
    }
  }, [isExpanded, persistState, storageKey]);
  
  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => {
      const newState = !prev;
      if (onToggle) onToggle(newState);
      return newState;
    });
  }, [onToggle]);

  const expand = useCallback(() => {
    setIsExpanded(true);
    if (onToggle) onToggle(true);
  }, [onToggle]);

  const collapse = useCallback(() => {
    setIsExpanded(false);
    if (onToggle) onToggle(false);
  }, [onToggle]);

  return {
    isExpanded,
    setIsExpanded,
    toggleExpanded,
    expand,
    collapse
  };
}
